import { useState } from "react";
import { projectsData, projectCategories, type ProjectCategory, type Project } from "@/mocks/projects";
import ProjectGalleryModal from "./ProjectGalleryModal";

const categoryLabel: Record<ProjectCategory, { zh: string; en: string }> = {
  ALL:         { zh: "全部",   en: "ALL" },
  RESIDENTIAL: { zh: "住宅",   en: "RESIDENTIAL" },
  COMMERCIAL:  { zh: "商业",   en: "COMMERCIAL" },
  CULTURAL:    { zh: "文化",   en: "CULTURAL" },
  HOSPITALITY: { zh: "酒店",   en: "HOSPITALITY" },
  LANDSCAPE:   { zh: "景观",   en: "LANDSCAPE" },
  OFFICE:      { zh: "办公",   en: "OFFICE" },
  BRAND_SPACE: { zh: "品牌空间及标准设计", en: "BRAND SPACE & STANDARD DESIGN" },
  RETAIL:      { zh: "零售",    en: "RETAIL" },
};

function ProjectCard({ project, onOpen }: { project: Project; onOpen: (p: Project) => void }) {
  const label = categoryLabel[project.type];
  const imgCount = project.gallery?.length ?? (project.hoverImage ? 2 : 1);

  return (
    <article className="group cursor-pointer" onClick={() => onOpen(project)}>
      <div className="relative overflow-hidden">
        {/* Primary Image */}
        <img
          src={project.image}
          alt={project.name}
          width={700}
          height={288}
          loading="lazy"
          decoding="async"
          className="w-full h-72 object-cover object-top transition-all duration-700 group-hover:scale-105"
        />
        {/* Hover Image */}
        {project.hoverImage && (
          <img
            src={project.hoverImage}
            alt={`${project.name} - detail`}
            width={700}
            height={288}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-72 object-cover object-top opacity-0 group-hover:opacity-100 transition-opacity duration-700 scale-105 group-hover:scale-100"
          />
        )}
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
        {/* Photo count badge */}
        <div
          className="absolute top-3 right-3 flex items-center gap-1 bg-black/50 px-2 py-1 text-white/80 text-xs tracking-widest"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          <i className="ri-image-line text-xs" />
          {imgCount}
        </div>
        {/* Overlay Content */}
        <div className="absolute inset-0 flex flex-col justify-end p-5">
          <div className="flex items-end justify-between">
            <h3
              className="text-white text-xl font-semibold leading-tight"
              style={{ fontFamily: "'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif" }}
            >
              {project.name}
            </h3>
            <div className="w-9 h-9 flex items-center justify-center bg-white/20 group-hover:bg-amber-700 rounded-full transition-colors duration-300 shrink-0 ml-3">
              <i className="ri-expand-diagonal-line text-white text-base" />
            </div>
          </div>
        </div>
      </div>
      {/* Info */}
      <div className="pt-4 space-y-1">
        <p className="text-xs tracking-[0.2em] text-amber-700 font-medium"
           style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {label.zh}&nbsp;<span className="text-amber-400/80 font-normal">/ {label.en}</span>
        </p>
        <p className="text-sm text-stone-700 font-medium"
           style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {project.location}
        </p>
        <p className="text-xs text-stone-400"
           style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {project.year}
        </p>
      </div>
    </article>
  );
}

export default function ProjectsSection() {
  const [activeCategory, setActiveCategory] = useState<ProjectCategory>("ALL");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const filtered = activeCategory === "ALL"
    ? projectsData
    : projectsData.filter((p) => p.type === activeCategory);

  return (
    <section id="projects" className="py-28 px-8 lg:px-20 bg-stone-50">
      <div className="max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div>
            <p className="text-xs tracking-[0.35em] text-stone-400 mb-3"
               style={{ fontFamily: "'DM Sans', sans-serif" }}>
              OUR WORK · 作品
            </p>
            <h2
              className="text-6xl font-black text-stone-900 leading-none"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              PROJECTS <span className="text-4xl text-stone-400">· 项目</span>
            </h2>
          </div>

          {/* Filter Tags */}
          <div className="flex flex-wrap items-center gap-2">
            {projectCategories.map((cat) => {
              const lbl = categoryLabel[cat];
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2 text-xs tracking-[0.12em] font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeCategory === cat
                      ? "bg-stone-900 text-white"
                      : "border border-stone-300 text-stone-600 hover:border-stone-900 hover:text-stone-900"
                  }`}
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {lbl.zh}&nbsp;<span className="opacity-50 font-normal">{lbl.en}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((project) => (
            <ProjectCard key={project.id} project={project} onOpen={setSelectedProject} />
          ))}
        </div>

        {/* View All */}
        <div className="mt-16 flex justify-center">
          <a
            href="#contact"
            className="flex items-center gap-3 border border-stone-900 px-10 py-4 text-xs tracking-[0.2em] text-stone-900 hover:bg-stone-900 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            VIEW ALL PROJECTS · 查看全部项目
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </a>
        </div>
      </div>

      {/* Gallery Modal */}
      <ProjectGalleryModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
      />
    </section>
  );
}
